import React from 'react';
import HelpApiLayout from '../../../../components/layout/HelpApiLayout';
import HelpSidebar from '../../../../components/layout/HelpSidebar';
import ArticleNavigation from '../../../../components/layout/docs/ArticleNavigation';
import LastUpdated from '../../../../components/layout/docs/LastUpdated';
import { getHelpArticleRoute } from '../../../../constants/routes';
import { getHelpRoute } from '../../../../utils/navigation';
import './HelpCategoryPage.css';

/**
 * Manage your group Article Page
 */
const ManageGroup = () => {
  return (
    <HelpApiLayout type="help" sidebar={<HelpSidebar />}>
      <div className="help-category-page">
        <div className="help-category-container">
          <main className="help-category-main">
            <div className="article-content">
              <header className="article-header">
                <h1 className="article-title">Manage your group</h1>
                <p className="article-subtitle">
                  Learn how to customize the look, manage members, assign permissions, and set financial goals.
                </p>
              </header>

              <div className="article-separator"></div>

              <section className="article-section">
                <div className="article-subsection">
                  <h2 className="article-section-title">Overview</h2>
                  <p className="article-section-intro">
                    Once your group is created, you have full control over its appearance and how it functions. From visual tweaks to advanced security permissions, here is how you can manage your group settings.
                  </p>
                </div>
              </section>

              <section className="article-section">
                <div className="article-subsection">
                  <h2 className="article-section-title">Visual Customization</h2>
                  <p className="article-section-intro">
                    Make your group easy to identify on your dashboard.
                  </p>
                </div>

                <div className="article-subsection">
                  <ul className="article-list-dots">
                    <li>
                      <strong>Name:</strong> Edit the group name at any time (e.g., change "Trip" to "Summer Ibiza 2024").
                    </li>
                    <li>
                      <strong>Icon:</strong> Choose a representative emoji or icon.
                    </li>
                    <li>
                      <strong>Color:</strong> Select a theme color for the group interface.
                    </li>
                  </ul>
                </div>
              </section>

              <section className="article-section">
                <div className="article-subsection">
                  <h2 className="article-section-title">Member Management</h2>
                </div>

                <div className="article-subsection">
                  <ul className="article-list-dots">
                    <li>
                      <strong>Add Members:</strong> Invite new participants to the shared wallet via username or link.
                    </li>
                    <li>
                      <strong>Remove Members:</strong> Remove participants who are no longer part of the group.
                    </li>
                  </ul>
                  <div className="article-note">
                    <p>Ensure all debts are settled before removing a member.</p>
                  </div>
                </div>
              </section>

              <section className="article-section">
                <div className="article-subsection">
                  <h2 className="article-section-title">Roles & Permissions</h2>
                  <p className="article-section-intro">
                    You can granularly modify what each member is allowed to do within the group. Available permissions include:
                  </p>
                </div>

                <div className="article-subsection">
                  <ul className="article-list-dots">
                    <li>
                      <strong>Admin:</strong> Grants full administrative control.
                    </li>
                    <li>
                      <strong>Add/Remove Members:</strong> Ability to manage the participant list.
                    </li>
                    <li>
                      <strong>Add Expense:</strong> Ability to log new transactions.
                    </li>
                    <li>
                      <strong>Top Up:</strong> Ability to fund the shared wallet.
                    </li>
                    <li>
                      <strong>Withdraw:</strong> Ability to send funds out of the wallet.
                    </li>
                    <li>
                      <strong>View Seed Phrase:</strong> Access to the wallet's private recovery key.
                    </li>
                  </ul>
                </div>
              </section>

              <section className="article-section">
                <div className="article-subsection">
                  <h2 className="article-section-title">Wallet Security</h2>
                  <h3 className="article-subsection-title">View Seed Phrase</h3>
                  <p className="article-section-intro">
                    For advanced recovery, you can access the shared wallet's credentials.
                  </p>
                </div>

                <div className="article-subsection">
                  <ul className="article-list-dots">
                    <li>
                      <strong>Path:</strong> Go to Settings &gt; View Seed Phrase.
                    </li>
                    <li>
                      <strong>Restriction:</strong> This feature is strictly limited to the Owner and Admins for security reasons. Never share this phrase outside the trusted leadership of the group.
                    </li>
                  </ul>
                </div>
              </section>

              <section className="article-section">
                <div className="article-subsection">
                  <h2 className="article-section-title">Advanced Features</h2>
                </div>

                <div className="article-subsection">
                  <ul className="article-list-dots">
                    <li>
                      <strong>Set a Goal:</strong> Define a target amount to reach (e.g., saving 1,000 USDC for a party). The group tracks the funding progress toward this goal.
                    </li>
                    <li>
                      <strong>Enable Degen Mode:</strong> Toggle this feature to unlock advanced, high-risk, or experimental crypto-native interactions within the group wallet.
                    </li>
                  </ul>
                </div>
              </section>

              <div className="article-separator"></div>

              <LastUpdated />

              <ArticleNavigation
                previous={{
                  title: 'Create your first group',
                  route: getHelpRoute(getHelpArticleRoute('getting-started', 'create-group'))
                }}
                next={{
                  title: 'Top up your group',
                  route: getHelpRoute(getHelpArticleRoute('getting-started', 'top-up-group'))
                }}
              />
            </div>
          </main>
        </div>
      </div>
    </HelpApiLayout>
  );
};

export default ManageGroup;
